import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  People as PeopleIcon,
  QuestionAnswer as QuestionIcon,
} from '@mui/icons-material';
import { surveyReportApi } from '../../survey-report/api/surveyReportApi';
import { ReportViewer } from '../../survey-report/components/ReportViewer';
import { ReportViewerSkeleton } from '../../survey-report/components/skeletons/ReportViewerSkeleton';
import { useSmartPdfExport } from '../../survey-report/hooks/useSmartPdfExport';
import { useReportBreadcrumb } from '../../survey-report/hooks/useReportBreadcrumb';
import { useSetPageActions } from '../../../contexts/PageActionsContext';
import { usePostHog } from '../../../hooks/usePostHog';
import { REPORT_VIEWING_EVENTS } from '../../../utils/trackingEvents';

const ReportViewPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { setActions } = useSetPageActions();
  const { captureEvent } = usePostHog();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Set breadcrumb metadata
  useReportBreadcrumb(reportId);

  const {
    data: report,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['surveyReport', reportId],
    queryFn: async () => {
      if (!reportId) throw new Error('Report ID is required');
      const response = await surveyReportApi.getSurveyReport(reportId);
      return response.data;
    },
    enabled: !!reportId,
  });

  // Smart PDF export hook: downloads existing PDF or generates + auto-downloads
  const { handleExport: smartExport, isExporting, phase, error: exportError } =
    useSmartPdfExport(reportId || '', report?.pdfExports ?? []);

  // Smart PDF print hook: opens existing PDF (or newly generated one) in new tab for printing
  const { handleExport: handlePrint, isExporting: isPrinting, phase: printPhase, error: printError } =
    useSmartPdfExport(reportId || '', report?.pdfExports ?? [], 'print');

  const handleExportPdf = async () => {
    captureEvent(REPORT_VIEWING_EVENTS.PDF_EXPORT_CLICKED, {
      report_id: reportId,
      action: 'export_pdf',
    });
    await smartExport();
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Show phase-based snackbar messages for export
  useEffect(() => {
    if (phase === 'generating' || phase === 'polling') {
      setSnackbar({
        open: true,
        message: 'Generating PDF... Download will start automatically.',
        severity: 'info',
      });
    } else if (phase === 'downloading') {
      setSnackbar({
        open: true,
        message: 'Downloading PDF...',
        severity: 'info',
      });
    } else if (phase === 'error' && exportError) {
      setSnackbar({
        open: true,
        message: exportError,
        severity: 'error',
      });
    } else if (phase === 'idle' && snackbar.open && snackbar.severity === 'info') {
      // Close info snackbar when returning to idle (download completed)
      setSnackbar((prev) => ({ ...prev, open: false }));
    }
  }, [phase, exportError]);

  // Show phase-based snackbar messages for print
  useEffect(() => {
    if (printPhase === 'generating' || printPhase === 'polling') {
      setSnackbar({
        open: true,
        message: 'Generating PDF... It will open for printing automatically.',
        severity: 'info',
      });
    } else if (printPhase === 'downloading') {
      setSnackbar({
        open: true,
        message: 'Opening PDF for printing...',
        severity: 'info',
      });
    } else if (printPhase === 'error' && printError) {
      setSnackbar({
        open: true,
        message: printError,
        severity: 'error',
      });
    } else if (printPhase === 'idle' && snackbar.open && snackbar.severity === 'info') {
      setSnackbar((prev) => ({ ...prev, open: false }));
    }
  }, [printPhase, printError]);

  // Register page actions when report data is available
  useEffect(() => {
    if (report) {
      setActions([
        {
          id: 'responses',
          type: 'chip',
          label: `${report.includedResponses} responses`,
          chipValue: `${report.includedResponses} responses`,
          icon: <PeopleIcon />,
        },
        {
          id: 'questions',
          type: 'chip',
          label: `${report.questionReports?.length || 0} questions`,
          chipValue: `${report.questionReports?.length || 0} questions`,
          icon: <QuestionIcon />,
        },
        {
          id: 'export-pdf',
          type: 'icon-button',
          label: 'Export PDF',
          icon: <PdfIcon />,
          onClick: handleExportPdf,
          disabled: isExporting || isPrinting,
        },
        {
          id: 'print',
          type: 'icon-button',
          label: 'Print',
          icon: <PrintIcon />,
          onClick: () => {
            captureEvent(REPORT_VIEWING_EVENTS.PDF_EXPORT_CLICKED, {
              report_id: reportId,
              action: 'print',
            });
            void handlePrint();
          },
          disabled: isExporting || isPrinting,
        },
      ]);
    }
  }, [report, setActions, captureEvent, reportId, isExporting, isPrinting, handlePrint]);

  if (isLoading) {
    return <ReportViewerSkeleton questionCount={4} showHeader={false} />;
  }

  if (error) {
    return (
      <Container maxWidth={false} disableGutters>
        <Alert severity="error">Failed to load report. Please try again.</Alert>
      </Container>
    );
  }

  if (!report) {
    return (
      <Container maxWidth={false} disableGutters>
        <Alert severity="warning">Report not found.</Alert>
      </Container>
    );
  }

  return (
    <>
      <ReportViewer report={report} />

      {/* Loading backdrop for PDF export */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isExporting || isPrinting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReportViewPage;
